import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useCallback, useRef } from "react";
import { Button, Card } from "@rumbledirect/ui-v2";
import { inviteEmployeeLabel } from "@/configurator/invites/configuratorInviteFields";
import {
  ConfiguratorRequestCreateForm,
  type SetFieldError,
} from "@/configurator/requests/ConfiguratorRequestCreateForm";
import { defaultConfiguratorRequestFormValues } from "@/configurator/requests/configuratorRequestForm";
import { toConfiguratorRequestPostInput } from "@/configurator/requests/configuratorRequestForm";
import { extractRequestViolations } from "@/configurator/requests/configuratorRequestViolations";
import type { ConfiguratorType } from "@/configurator/requests/configuratorRequest";
import { useCreateConfiguratorRequest } from "@/configurator/requests/useCreateConfiguratorRequest";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorInvitesConfiguratorInviteId } from "@/lib/api/generated/configurator/configurator";

const CREATE_PERMISSION = "admin_configurator_request_create";

interface RequestCreateSearch {
  /** The invite being answered, if the page was opened from one. */
  readonly invite?: string;
  /**
   * Which kind of request to start on. A contract expiration knows this from
   * the choice that was recorded on it - a replacement composed here or one
   * drawn up elsewhere - so it says so rather than letting the user pick it
   * again and pick it wrong.
   */
  readonly type?: ConfiguratorType;
  /** The vehicle being replaced, passed through from the expiration. */
  readonly oldVehicle?: string;
  /**
   * The expiring contract; the employee is selected by hand.
   */
  readonly contract?: string;
}

function asConfiguratorType(value: unknown): ConfiguratorType | undefined {
  return value === "internal" || value === "external" ? value : undefined;
}

function asId(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/requests_/new",
)({
  validateSearch: (search: Record<string, unknown>): RequestCreateSearch => ({
    invite: typeof search.invite === "string" ? search.invite : undefined,
    type: asConfiguratorType(search.type),
    oldVehicle: asId(search.oldVehicle),
    contract: asId(search.contract),
  }),
  component: ConfiguratorRequestCreatePage,
});

/**
 * Hands a configuration in on behalf of a driver.
 *
 * Opened either from the overview, where a driver still has to be picked, or
 * from an invite via `?invite=`, and then the invite names the driver and seeds
 * the terms it was sent with. Without an invite the backend creates one
 * alongside the request, so a declined request can still be handed back to the
 * driver to configure himself.
 *
 * A contract expiration opens it a third way, without an invite but with what
 * it already knows: which kind of request its recorded choice calls for and
 * which contract it settles. The invite
 * the backend creates alongside then names that contract, which is how the
 * request finds its way back to the expiration.
 */
function ConfiguratorRequestCreatePage(): ReactNode {
  const { invite: inviteId, type, oldVehicle, contract } = Route.useSearch();
  const navigate = useNavigate();
  const setFieldError = useRef<SetFieldError>(undefined);

  const { data: invite, isPending: invitePending } =
    useGetAdminConfiguratorInvitesConfiguratorInviteId(inviteId ?? "", {
      query: { enabled: Boolean(inviteId) },
    });

  const createRequest = useCreateConfiguratorRequest();

  const onFormReady = useCallback((setError: SetFieldError) => {
    setFieldError.current = setError;
  }, []);

  // Waiting for the invite rather than seeding an empty form and correcting it
  // later, which would throw away anything already typed.
  if (inviteId !== undefined && invitePending) {
    return (
      <Card
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="p-6 text-sm text-muted-foreground"
      >
        Bezig met laden…
      </Card>
    );
  }

  return (
    <RequirePermission permission={CREATE_PERMISSION}>
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void navigate({
                to: "/configurator/requests",
                search: { page: 1 },
              })
            }
            className="-ml-2"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Terug naar aanvragen
          </Button>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">
            Nieuwe aanvraag
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Stel een voertuig samen en dien het in namens een medewerker.
          </p>
        </div>

        <ConfiguratorRequestCreateForm
          defaultValues={defaultConfiguratorRequestFormValues({
            configuratorType: type ?? "internal",
            invite: invite?.id ?? "",
            contract: inviteId === undefined ? (contract ?? "") : "",
            oldVehicle: invite?.oldVehicle ?? oldVehicle ?? "",
            vehicleConversion: "",
            expectedKilometers:
              invite?.expectedKilometers === null ||
              invite?.expectedKilometers === undefined
                ? ""
                : String(invite.expectedKilometers),
            contractDurationMonths:
              invite?.contractDurationMonths === null ||
              invite?.contractDurationMonths === undefined
                ? ""
                : String(invite.contractDurationMonths),
            desiredDeliveryAt: invite?.desiredDeliveryAt ?? "",
            // A vehicle to replace is only ever named because it is being
            // replaced, so naming one is answer enough to the question whether
            // this is a replacement.
            replacement: invite?.replacement ?? oldVehicle !== undefined,
          })}
          inviteEmployeeLabel={invite ? inviteEmployeeLabel(invite) : undefined}
          isSubmitting={createRequest.isPending}
          onFormReady={onFormReady}
          onSubmit={(values) => {
            createRequest.mutate(toConfiguratorRequestPostInput(values), {
              // The API always answers with an id; if it somehow does not,
              // land on the overview rather than leaving the form standing as
              // though nothing happened.
              onSuccess: (created) => {
                if (!created.id) {
                  void navigate({
                    to: "/configurator/requests",
                    search: { page: 1 },
                  });
                  return;
                }

                void navigate({
                  to: "/configurator/requests/$configuratorRequestId",
                  params: { configuratorRequestId: created.id },
                  search: { tab: "algemeen" },
                });
              },
              // The hook already says that submitting failed; this only points
              // at where, so the message is not repeated.
              onError: (error) => {
                for (const violation of extractRequestViolations(error)) {
                  setFieldError.current?.(violation.field, violation.message);
                }
              },
            });
          }}
        />
      </div>
    </RequirePermission>
  );
}
